<template>
  <div class="fts-editor">
    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
      <div class="menubar">
        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.bold() }"
          @click="commands.bold"
        >
          <font-awesome-icon :icon="['fas', 'bold']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.italic() }"
          @click="commands.italic"
        >
          <font-awesome-icon :icon="['fas', 'italic']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.strike() }"
          @click="commands.strike"
        >
          <font-awesome-icon :icon="['fas', 'strikethrough']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.underline() }"
          @click="commands.underline"
        >
          <font-awesome-icon :icon="['fas', 'underline']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.paragraph() }"
          @click="commands.paragraph"
        >
          <font-awesome-icon :icon="['fas', 'paragraph']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click="commands.heading({ level: 1 })"
        >H1</button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click="commands.heading({ level: 2 })"
        >H2</button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          @click="commands.heading({ level: 3 })"
        >H3</button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click="commands.bullet_list"
        >
          <font-awesome-icon :icon="['fas', 'list-ul']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click="commands.ordered_list"
        >
          <font-awesome-icon :icon="['fas', 'list-ol']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.superscript() }"
          @click="commands.superscript"
        >
          <font-awesome-icon :icon="['fas', 'superscript']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.subscript() }"
          @click="commands.subscript"
        >
          <font-awesome-icon :icon="['fas', 'subscript']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          @click="show = true"
        >
          <font-awesome-icon :icon="['fas', 'code']" fixed-width />
          <fts-release-badge class="fts-align-top" />
        </button>
      </div>
    </editor-menu-bar>

    <editor-content
      class="editor__content fts-editor__content"
      :editor="editor"
      :key="uid"
      ref="editorContent"
      v-if="editor"
    />

    <!-- code viewer -->
    <fts-modal title="HTML Editor" @close="show=false" v-if="show">
      <template #body>
        <fts-script-editor editor-id="ftsContentScriptEditor" v-model="formData[item.fieldName]" />
      </template>
    </fts-modal>
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
  // Blockquote,
  // CodeBlock,
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  // Code,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Placeholder,
} from "tiptap-extensions";

import { Superscript } from 'tiptap-extension-superscript';
import { Subscript } from 'tiptap-extension-subscript';

import { store as canvasDesignStore } from "../store/canvasDesignStore";

import FTSModal from "./FTSModal";
import FTSScriptEditor from "../design/FTSScriptEditor";
import FTSReleaseBadge from "./FTSReleaseBadge";

export default {
  components: {
    EditorContent,
    EditorMenuBar,
    "fts-modal": FTSModal,
    "fts-script-editor": FTSScriptEditor,
    "fts-release-badge": FTSReleaseBadge,
  },
  props: {
    item: Object,
    formData: Object,
    data: String,
  },
  data: () => ({
    uid: null,
    editor: null,
    editorChange: false,
    show: false,
  }),
  methods: {
    updateContentHeight() {
      this.$nextTick(() => {
        try {
          const elOffsetHeight = this.$refs.editorContent.$el.offsetHeight;
          elOffsetHeight && this.$emit('updateContentHeight', `${elOffsetHeight}px`);
        } catch(err) {
          console.warn('[DOMException]', err);
        }
      });
    },
  },
  computed: {
    beta() {
      return canvasDesignStore.state.beta;
    },
  },
  watch: {
    data() {
      if (this.editor && !this.editorChange) {
        let val = this.formData[this.item.fieldName] || '';
        this.editor.setContent(val, false);
      }
      this.editorChange = false;
    }
  },
  mounted() {
    this.uid = this._uid
    this.editor = new Editor({
      extensions: [
        // new Blockquote(),
        new BulletList(),
        // new CodeBlock(),
        new HardBreak(),
        new Heading({ levels: [1, 2, 3] }),
        new HorizontalRule(),
        new ListItem(),
        new OrderedList(),
        new TodoItem(),
        new TodoList(),
        new Link(),
        new Bold(),
        // new Code(),
        new Italic(),
        new Strike(),
        new Underline(),
        new History(),
        new Superscript(),
        new Subscript(),
        new Placeholder({
          emptyEditorClass: 'is-editor-empty',
          emptyNodeClass: 'is-empty',
          showOnlyWhenEditable: true,
          showOnlyCurrent: true,
          emptyNodeText: (node) => {
            return "write something...";
          },
        }),
      ],
      content: this.data,
      parseOptions: {
        preserveWhitespace: 'full',
      },
      onUpdate: ({ getHTML }) => {
        /* to-do break-line issue */
        const state = getHTML();  
        this.$set(this.formData, this.item.fieldName, state);
        this.$emit('input', state);
        this.editorChange = true;
        this.updateContentHeight();
      }
    });
    this.$nextTick(() => {
      this.updateContentHeight();
    });
  },
  beforeDestroy() {
    this.editor && this.editor.destroy();
  }
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-editor {
  background: var(--fts-form-control-background);
  color: var(--fts-form-control);
  .menubar {
    display: flex;
    flex-wrap: wrap;
    padding: 4px;
    background: var(--fts-gray-1);
    button {
      margin: 2px;
      padding: 0.275rem 0.575rem;
      background: var(--fts-background);
      color: var(--fts-gray-5);
      border: 1px solid transparent;
      border-radius: 0.125rem;
      display: inline-block;
      font-weight: bold;
      font-size: 0.75rem;
      text-align: center;
      vertical-align: middle;
      white-space: nowrap;
      line-height: 1;
      @include user-select-none;
      cursor: pointer;
      &.is-active {
        color: $primaryColor;
      }
    }
  }
  .editor__content {    
    min-height: $height;
    border: 1px solid var(--fts-form-control-border);
    font-weight: initial;
    text-decoration: initial;
    color: var(--fts-text);
    &:focus-within {
      border-color: $primaryColor;
      box-shadow: $inputBoxShadow;
    }
    .ProseMirror {
      flex: 1;
      padding: .375rem .75rem;
      line-height: 1.5;
      & *.is-empty:nth-child(1)::before {
        content: attr(data-empty-text);
        float: left;
        color: var(--fts-form-control-placeholder);
        pointer-events: none;
        height: 0;
      }
    }
  }
}
</style>

